import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard-layout";
import { useContext, useState, useEffect } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { UserTable } from "@/routes/_dashboard/users/-components/user-table";
import { usersQueryOptions } from "@/server/users";
import { useSearch } from "@tanstack/react-router";
import { SidebarUser } from "./-components/user-sidebar";
import { organisationUsersQueryOptions } from "@/server/users";
import { useMemo } from "react";
import { closeFullscreenDialog } from "@/components/fullscreen-sidebar";
import { toast } from "sonner";
import { minuteCounterOverridesQueryOptions } from "@/server/settings";

export const Route = createFileRoute("/_dashboard/users")({
  component: () => (
    <DashboardLayout>
      <UsersRouteComponent />
    </DashboardLayout>
  ),
  beforeLoad: () => {
    return { title: "Users" };
  },
  loader: () => ({
    crumb: "Users",
  }),
});

function UsersRouteComponent() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as {
    id?: string;
    search?: string;
    organisation?: number;
    expire?: number;
    tab?: string;
  };
  const [search, setSearch] = useState(searchParams.search);
  const [organisation, setOrganisation] = useState(searchParams.organisation);
  const [expire, setExpire] = useState(searchParams.expire);
  const queryClient = useQueryClient();
  const organisationUsersQueryOptionsMemo = useMemo(
    () => organisationUsersQueryOptions(Number(organisation ?? 0), search, expire),
    [organisation, search, expire]
  );
  const organisationUsersQuery = useQuery(organisationUsersQueryOptionsMemo);
  const organisationUsers = organisationUsersQuery.data;
  const usersQueryOptionsMemo = useMemo(() => usersQueryOptions(search, expire), [search, expire]);
  const usersQuery = useQuery(usersQueryOptionsMemo);
  const users = usersQuery.data;
  const minuteCounterOverridesQueryOptionsMemo = useMemo(() => minuteCounterOverridesQueryOptions, []);
  const minuteCounterOverridesQuery = useQuery(minuteCounterOverridesQueryOptionsMemo);
  const minuteCounterOverrides = minuteCounterOverridesQuery.data?.minuteCounterOverrides;

  const [isContextReady, setIsContextReady] = useState(false);

  const context = useContext(DashboardContext);

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  useEffect(() => {
    setSearch(searchParams.search);
    setOrganisation(searchParams.organisation);
    const expireDate = new Date(searchParams.expire ?? "");
    if (isNaN(expireDate.getTime())) {
      setExpire(undefined);
    } else {
      setExpire(parseInt(expireDate.getTime().toFixed(0)));
    }
  }, [searchParams]);

  const closeAfterSave = () => {
    closeFullscreenDialog();
    context.setRightSidebar(null);
    navigate({
      to: "/users",
      replace: true,
      search: (prev) => {
        const next = { ...(prev || {}) } as Record<string, unknown>;
        delete next.id;
        delete next.tab;
        delete next.transactionsPage;
        delete next.transactionsPerPage;
        delete next.transactionsSortBy;
        delete next.transactionsSortDir;
        return Object.keys(next).length > 0 ? next : undefined;
      },
    });
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(organisation ?? 0);
      },
    });
  };

  const handleUserAction = () => {
    invalidateQueries();
    closeAfterSave();
  };

  useEffect(() => {
    if (usersQuery.isError) {
      const message =
        usersQuery.error instanceof Error ? usersQuery.error.message : "Failed to load users. Please try again later.";
      toast.error(message);
    }
  }, [usersQuery.isError, usersQuery.error]);

  useEffect(() => {
    if (!isContextReady || !context?.setRightSidebar) {
      return;
    }
    if (searchParams.id && users && users.length) {
      const user = users.find((u) => u.id === searchParams.id);
      if (user) {
        context.setRightSidebar(
          <SidebarUser
            userId={user.id}
            organisationId={user.organisation}
            onUserUpdated={handleUserAction}
            onUserDeleted={handleUserAction}
            closeAfterSave={closeAfterSave}
            minuteCounterOverrides={minuteCounterOverrides ?? null}
          />
        );
        context.setRightSidebarParamKey?.([
          "id",
          "tab",
          "transactionsPage",
          "transactionsPerPage",
          "transactionsSortBy",
          "transactionsSortDir",
        ]);
      }
    }
    if (searchParams.id === "new") {
      context.setRightSidebar(
        <SidebarUser
          userId={null}
          organisationId={null}
          onUserCreated={handleUserAction}
          minuteCounterOverrides={minuteCounterOverrides ?? null}
          closeAfterSave={closeAfterSave}
        />
      );
      context.setRightSidebarParamKey?.([
        "id",
        "tab",
        "transactionsPage",
        "transactionsPerPage",
        "transactionsSortBy",
        "transactionsSortDir",
      ]);
    }
  }, [searchParams.id, isContextReady, users]);

  const handleAddUser = () => {
    navigate({
      to: "/users",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <UserTable
      users={organisation && organisation > 0 ? organisationUsers : users}
      handleAddUser={handleAddUser}
      isLoading={usersQuery.isLoading}
    />
  );
}
